import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { X, FileText, Shield, Cookie, ExternalLink, Check, AlertTriangle, Lock, UserCheck, Mic, Video, Brain, MessageSquare, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import api from '@/api/client'

interface ConsentText {
  version: string
  title: string
  effectiveDate: string
  content: string
}

interface ConsentTexts {
  terms: ConsentText
  privacy: ConsentText
  cookies: ConsentText
}

interface ConsentModalProps {
  open: boolean
  onAccept: (consents: { termsAccepted: boolean; privacyAccepted: boolean; cookieAccepted: boolean; marketingEmails: boolean }) => void
  onCancel?: () => void
  required?: boolean
}

export default function ConsentModal({ open, onAccept, onCancel, required = true }: ConsentModalProps) {
  const { t } = useTranslation()
  const [texts, setTexts] = useState<ConsentTexts | null>(null)
  const [viewing, setViewing] = useState<'terms' | 'privacy' | 'cookies' | null>(null)
  const [consents, setConsents] = useState({
    termsAccepted: false,
    privacyAccepted: false,
    cookieAccepted: false,
    marketingEmails: false,
  })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (open && !texts) {
      setLoading(true)
      api.get('/consent/text')
        .then(r => setTexts(r.data))
        .catch(e => console.error('Failed to load consent texts:', e))
        .finally(() => setLoading(false))
    }
  }, [open, texts])

  useEffect(() => {
    if (!open) {
      setViewing(null)
    }
  }, [open])

  if (!open) return null

  const allRequiredAccepted = consents.termsAccepted && consents.privacyAccepted && consents.cookieAccepted

  if (viewing && texts) {
    const doc = texts[viewing]
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setViewing(null)} />
        <div className="relative w-full max-w-3xl max-h-[90vh] rounded-2xl bg-card border border-border shadow-2xl overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-primary/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                {viewing === 'terms' && <FileText className="w-5 h-5 text-primary" />}
                {viewing === 'privacy' && <Lock className="w-5 h-5 text-primary" />}
                {viewing === 'cookies' && <Cookie className="w-5 h-5 text-primary" />}
              </div>
              <div>
                <h2 className="text-lg font-semibold">{doc.title}</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t('consent.version')}: {doc.version} • {doc.effectiveDate}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setViewing(null)}
              className="p-2 rounded-lg hover:bg-secondary transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6">
            <ConsentDocument content={doc.content} />
          </div>
          <div className="px-6 py-4 border-t border-border bg-secondary/20 flex justify-end">
            <Button onClick={() => setViewing(null)}>
              {t('common.close')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !required && onCancel?.()} />
      <div className="relative w-full max-w-xl rounded-2xl bg-card border border-border shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border bg-primary/5">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">{t('consent.title')}</h2>
          </div>
          <p className="text-xs text-muted-foreground">
            {required ? t('consent.mustAccept') : t('consent.optionalAccept')}
          </p>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {loading || !texts ? (
            <div className="py-12 text-center text-muted-foreground">
              {t('common.loading')}...
            </div>
          ) : (
            <>
              {/* Info banner */}
              <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/5 border border-amber-500/20 text-xs text-amber-700">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <p>
                  {t('consent.infoBanner')}
                </p>
              </div>

              {/* Terms */}
              <ConsentCard
                icon={FileText}
                color="blue"
                title={t('consent.terms')}
                required
                checked={consents.termsAccepted}
                onChange={(v) => setConsents(c => ({ ...c, termsAccepted: v }))}
                onView={() => setViewing('terms')}
                highlights={[
                  { icon: UserCheck, text: t('consent.highlights.communityRules') },
                  { icon: Brain, text: t('consent.highlights.aiModeration') },
                  { icon: Video, text: t('consent.highlights.jitsiEvents') },
                  { icon: MessageSquare, text: t('consent.highlights.ephemeralChat') },
                ]}
              />

              {/* Privacy */}
              <ConsentCard
                icon={Lock}
                color="emerald"
                title={t('consent.privacy')}
                required
                checked={consents.privacyAccepted}
                onChange={(v) => setConsents(c => ({ ...c, privacyAccepted: v }))}
                onView={() => setViewing('privacy')}
                highlights={[
                  { icon: Shield, text: t('consent.highlights.kvkkGdpr') },
                  { icon: MessageSquare, text: t('consent.highlights.e2eEncryption') },
                  { icon: Clock, text: t('consent.highlights.dataRetention') },
                  { icon: UserCheck, text: t('consent.highlights.yourRights') },
                ]}
              />

              {/* Cookies */}
              <ConsentCard
                icon={Cookie}
                color="amber"
                title={t('consent.cookies')}
                required
                checked={consents.cookieAccepted}
                onChange={(v) => setConsents(c => ({ ...c, cookieAccepted: v }))}
                onView={() => setViewing('cookies')}
                highlights={[
                  { icon: Shield, text: t('consent.highlights.sessionCookies') },
                  { icon: Mic, text: t('consent.highlights.noThirdPartyAds') },
                ]}
              />

              {/* Marketing */}
              <div className="flex items-start gap-3 p-4 rounded-xl border border-border bg-secondary/20 hover:bg-secondary/30 transition-colors cursor-pointer"
                onClick={() => setConsents(c => ({ ...c, marketingEmails: !c.marketingEmails }))}
              >
                <input
                  type="checkbox"
                  checked={consents.marketingEmails}
                  onChange={(e) => setConsents(c => ({ ...c, marketingEmails: e.target.checked }))}
                  className="mt-0.5 w-4 h-4 rounded text-primary focus:ring-primary/50 cursor-pointer"
                />
                <div>
                  <p className="text-sm font-medium">{t('consent.marketingTitle')}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t('consent.marketing')}</p>
                </div>
              </div>

              {/* AI Moderation — mandatory */}
              <div className="flex items-start gap-3 p-4 rounded-xl border border-primary/20 bg-primary/5">
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  className="mt-0.5 w-4 h-4 rounded text-primary"
                />
                <div>
                  <p className="text-sm font-medium">{t('consent.aiModerationTitle')}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t('consent.aiModeration')}</p>
                </div>
              </div>

              {/* Summary */}
              <div className="p-3 rounded-lg bg-secondary/30 text-xs text-muted-foreground text-center">
                {t('consent.allRequired')}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border bg-secondary/20 flex gap-3 justify-end">
          {!required && onCancel && (
            <Button variant="secondary" onClick={onCancel}>
              {t('common.cancel')}
            </Button>
          )}
          <Button
            disabled={!allRequiredAccepted}
            onClick={() => onAccept(consents)}
          >
            <Check className="w-4 h-4 mr-1.5" />
            {t('consent.accept')}
          </Button>
        </div>
      </div>
    </div>
  )
}

function ConsentDocument({ content }: { content: string }) {
  const lines = content.trim().split('\n')
  const elements: React.ReactNode[] = []
  let currentTable: string[][] | null = null
  let listItems: string[] = []
  let inList = false

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`list-${elements.length}`} className="space-y-2 my-3 ml-1">
          {listItems.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-foreground/80">
              <span className="w-1.5 h-1.5 rounded-full bg-primary/60 mt-2 shrink-0" />
              <span dangerouslySetInnerHTML={{ __html: formatInline(item) }} />
            </li>
          ))}
        </ul>
      )
      listItems = []
      inList = false
    }
  }

  const flushTable = () => {
    if (currentTable && currentTable.length > 0) {
      const headers = currentTable[0]
      const rows = currentTable.slice(1)
      elements.push(
        <div key={`table-${elements.length}`} className="overflow-x-auto my-4 rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50">
              <tr>
                {headers.map((h, i) => (
                  <th key={i} className="text-left px-4 py-2.5 font-semibold text-xs uppercase tracking-wide text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((row, ri) => (
                <tr key={ri} className="hover:bg-secondary/20">
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-4 py-2.5 text-foreground/80" dangerouslySetInnerHTML={{ __html: formatInline(cell) }} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
      currentTable = null
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) {
      flushList()
      flushTable()
      continue
    }

    // Table row detection
    if (line.startsWith('|') && line.endsWith('|')) {
      flushList()
      const cells = line.slice(1, -1).split('|').map(c => c.trim()).filter(c => c && !c.match(/^[-:]+$/))
      if (cells.length > 0) {
        if (!currentTable) currentTable = []
        currentTable.push(cells)
      }
      continue
    } else if (currentTable) {
      flushTable()
    }

    // Heading detection
    if (line.startsWith('**') && line.endsWith('**') && !line.includes('**', 2)) {
      flushList()
      elements.push(
        <h3 key={`h-${elements.length}`} className="text-base font-bold text-foreground mt-6 mb-3 first:mt-0">
          {line.replace(/\*\*/g, '')}
        </h3>
      )
      continue
    }

    // List item detection
    if (line.startsWith('- ') || line.startsWith('• ')) {
      inList = true
      listItems.push(line.slice(2))
      continue
    } else if (inList) {
      flushList()
    }

    // Numbered list
    const numberedMatch = line.match(/^\d+\.\s+/)
    if (numberedMatch) {
      flushList()
      elements.push(
        <div key={`p-${elements.length}`} className="flex items-start gap-2 text-sm text-foreground/80 my-2">
          <span className="font-semibold text-primary/80 shrink-0">{line.match(/^\d+/)?.[0]}.</span>
          <span dangerouslySetInnerHTML={{ __html: formatInline(line.replace(/^\d+\.\s+/, '')) }} />
        </div>
      )
      continue
    }

    // Regular paragraph
    elements.push(
      <p key={`p-${elements.length}`} className="text-sm text-foreground/80 leading-relaxed my-2"
        dangerouslySetInnerHTML={{ __html: formatInline(line) }}
      />
    )
  }

  flushList()
  flushTable()

  return <div className="space-y-1">{elements}</div>
}

function formatInline(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-foreground">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code class="px-1 py-0.5 rounded bg-secondary text-xs font-mono">$1</code>')
}

function ConsentCard({
  icon: Icon,
  color,
  title,
  required,
  checked,
  onChange,
  onView,
  highlights,
}: {
  icon: any
  color: 'blue' | 'emerald' | 'amber'
  title: string
  required: boolean
  checked: boolean
  onChange: (v: boolean) => void
  onView: () => void
  highlights: { icon: any; text: string }[]
}) {
  const colorClasses = {
    blue: 'border-blue-500/20 bg-blue-500/5 text-blue-500',
    emerald: 'border-emerald-500/20 bg-emerald-500/5 text-emerald-500',
    amber: 'border-amber-500/20 bg-amber-500/5 text-amber-500',
  }

  const checkedClasses = {
    blue: 'border-primary/40 bg-primary/5',
    emerald: 'border-primary/40 bg-primary/5',
    amber: 'border-primary/40 bg-primary/5',
  }

  return (
    <div className={`rounded-xl border ${checked ? checkedClasses[color] : 'border-border bg-secondary/20'} transition-all overflow-hidden`}>
      <div className="flex items-start gap-3 p-4">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-1 w-4 h-4 rounded text-primary focus:ring-primary/50 cursor-pointer"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Icon className={`w-4 h-4 ${colorClasses[color].split(' ').pop()}`} />
            <span className="text-sm font-semibold">
              {title} {required && <span className="text-destructive">*</span>}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
            {highlights.map((h, i) => (
              <div key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <h.icon className="w-3 h-3 shrink-0" />
                <span>{h.text}</span>
              </div>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={onView}
          className="shrink-0 text-xs text-primary hover:underline flex items-center gap-1 mt-1"
        >
          <ExternalLink className="w-3 h-3" />
        Oku
        </button>
      </div>
    </div>
  )
}
